<script lang="ts">
	import { devices_settings } from 'src/stores/device-runtime/status';
	import { format } from 'svelte-i18n';
	import { real_id } from 'src/helpers/interface_helpers';
	import { onSettingsResponse } from 'src/helpers/device_settings_helpers';
	import { gen2CloudReq } from 'src/services/gen_2_http_reqs';
	import { getDeviceStatus, readDeviceSettings } from 'src/stores/device-runtime/status-selectors';
	import { Alert, Button, Input, Stack, Text, Container } from '@Shelly/shelly-design-system';
	import type { Device } from 'src/types/Device';
	import { showNewLayout } from 'src/stores/toDelete/new_layout';

	export let device: Device;

	const id = real_id(device.id);
	const settings = readDeviceSettings(id);

	let waitForResponse = false;

	let snr = settings.presence?.sensor?.snr ?? 50;
	let points = settings.presence?.sensor?.points ?? 40;
	let velocity = settings.presence?.sensor?.velocity ?? 0.01;
	let maxVelocity = settings.presence?.sensor?.max_velocity ?? 20;
	let detActThr = settings.presence?.sensor?.state?.det_act_thr ?? 3;
	let detFreeThr = settings.presence?.sensor?.state?.det_free_thr ?? 3;
	let actFreeThr = settings.presence?.sensor?.state?.act_free_thr ?? 12;
	let statFreeThr = settings.presence?.sensor?.state?.stat_free_thr ?? 50;
	let sleepFreeThr = settings.presence?.sensor?.state?.sleep_free_thr ?? 100;

	const save = async () => {
		waitForResponse = true;
		const res = await gen2Request(
			device,
			'Presence.SetConfig',
			{
				config: {
					sensor: {
						snr: Number(snr),
						points: Number(points),
						velocity: Number(velocity),
						max_velocity: Number(maxVelocity),
						state: {
							det_act_thr: Number(detActThr),
							det_free_thr: Number(detFreeThr),
							act_free_thr: Number(actFreeThr),
							stat_free_thr: Number(statFreeThr),
							sleep_free_thr: Number(sleepFreeThr),
						},
					},
				},
			},
			true,
		);
		onSettingsResponse(res, device);
		if (res.isok) {
			if (!settings.presence) settings.presence = {};
			if (!settings.presence.sensor) settings.presence.sensor = {};
			settings.presence.sensor.snr = Number(snr);
			settings.presence.sensor.points = Number(points);
			settings.presence.sensor.velocity = Number(velocity);
			settings.presence.sensor.max_velocity = Number(maxVelocity);
			if (!settings.presence.sensor.state) settings.presence.sensor.state = {};
			settings.presence.sensor.state.det_act_thr = Number(detActThr);
			settings.presence.sensor.state.det_free_thr = Number(detFreeThr);
			settings.presence.sensor.state.act_free_thr = Number(actFreeThr);
			settings.presence.sensor.state.stat_free_thr = Number(statFreeThr);
			settings.presence.sensor.state.sleep_free_thr = Number(sleepFreeThr);
			devices_settings.update_devices_settings(id, settings);
		}
		waitForResponse = false;
	};
</script>

<Stack gap="s" class="mt-s">
	<Alert
		type="info"
		heading={$format('presence_fine_tuning_frame_info', {
			default: '1 frame = 100 ms (10 frames per second)',
		})}
	/>

	<Text styleType="body" class="mb-xs mt-s">
		{$format('presence_fine_tuning_snr_label', { default: 'SNR threshold' })}
	</Text>
	<Text styleType="body-small" class="text-color-muted mb-s">
		{$format('presence_fine_tuning_snr_tooltip', {
			default:
				'Set the minimum signal strength to detect motion. Lower values increase sensitivity; higher values reduce false detections. Range: 10 to 100.',
		})}
	</Text>
	<Input
		type="number"
		min="10"
		max="100"
		step="1"
		bind:value={snr}
		label={$format('presence_fine_tuning_snr_label', { default: 'SNR threshold' })}
		id="input-snr-settings"
		aria-labelledby="input-snr-settings"
	/>

	<Text styleType="body" class="mb-xs mt-s">
		{$format('presence_fine_tuning_points_label', {
			default: 'Object recognition threshold',
		})}
	</Text>
	<Text styleType="body-small" class="text-color-muted mb-s">
		{$format('presence_fine_tuning_points_tooltip', {
			default:
				'Set the points required to recognize an object. Lower values allow detection of smaller or partially visible objects. Range: 10 to 100 points.',
		})}
	</Text>
	<Input
		type="number"
		min="10"
		max="100"
		step="1"
		bind:value={points}
		label={$format('presence_fine_tuning_points_label', {
			default: 'Object recognition threshold',
		})}
		id="input-points-settings"
		aria-labelledby="input-points-settings"
	/>

	<Text styleType="body" class="mb-xs mt-s">
		{$format('presence_fine_tuning_velocity_label', { default: 'Velocity threshold' })}
	</Text>
	<Text styleType="body-small" class="text-color-muted mb-s">
		{$format('presence_fine_tuning_velocity_tooltip', {
			default:
				'Set the minimum speed to detect an object. Use low values to better detect slow movement. Use high values to ignore slow or stationary objects. Range: 0.01 m to 1 m per second.',
		})}
	</Text>
	<Input
		type="number"
		min="0.01"
		max="1"
		step="0.01"
		bind:value={velocity}
		label={$format('presence_fine_tuning_velocity_label', { default: 'Velocity threshold' })}
		id="input-velocity-settings"
		aria-labelledby="input-velocity-settings"
	/>

	<Text styleType="body" class="mb-xs mt-s">
		{$format('presence_fine_tuning_max_velocity_label', {
			default: 'Max velocity difference',
		})}
	</Text>
	<Text styleType="body-small" class="text-color-muted mb-s">
		{$format('presence_fine_tuning_max_velocity_tooltip', {
			default:
				'Set the speed variance for grouping points as one target. Prevents unrelated motion (e.g., one object moving past another) from being grouped together. Range: 1 m to 50 m per second.',
		})}
	</Text>
	<Input
		type="number"
		min="1"
		max="50"
		step="1"
		bind:value={maxVelocity}
		label={$format('presence_fine_tuning_max_velocity_label', {
			default: 'Max velocity difference',
		})}
		id="input-max-velocity-settings"
		aria-labelledby="input-max-velocity-settings"
	/>

	<Text styleType="body" class="mb-xs mt-s">
		{$format('presence_fine_tuning_det_act_label', {
			default: 'Motion activation threshold (det_act_thr)',
		})}
	</Text>
	<Text styleType="body-small" class="text-color-muted mb-s">
		{$format('presence_fine_tuning_det_act_tooltip', {
			default:
				'Set the frames required to mark an object as active. Lower values react faster to motion; higher values improve stability and reduce false triggers. Range: 1 to 100 frames.',
		})}
	</Text>
	<Input
		type="number"
		min="1"
		max="100"
		step="1"
		bind:value={detActThr}
		label={$format('presence_fine_tuning_det_act_label', {
			default: 'Motion activation threshold (det_act_thr)',
		})}
		id="input-det-act-thr-settings"
		aria-labelledby="input-det-act-thr-settings"
	/>

	<Text styleType="body" class="mb-xs mt-s">
		{$format('presence_fine_tuning_det_free_label', {
			default: 'Motion release threshold (det_free_thr)',
		})}
	</Text>
	<Text styleType="body-small" class="text-color-muted mb-s">
		{$format('presence_fine_tuning_det_free_tooltip', {
			default:
				'Set the frames required to clear a detection. Lower values stop detection quickly; higher values help avoid flickering. Range: 1 to 100 frames.',
		})}
	</Text>
	<Input
		type="number"
		min="1"
		max="100"
		step="1"
		bind:value={detFreeThr}
		label={$format('presence_fine_tuning_det_free_label', {
			default: 'Motion release threshold (det_free_thr)',
		})}
		id="input-det-free-thr-settings"
		aria-labelledby="input-det-free-thr-settings"
	/>

	<Text styleType="body" class="mb-xs mt-s">
		{$format('presence_fine_tuning_act_free_label', {
			default: 'Tracking loss threshold (act_free_thr)',
		})}
	</Text>
	<Text styleType="body-small" class="text-color-muted mb-s">
		{$format('presence_fine_tuning_act_free_tooltip', {
			default:
				'Set the frames required for an active object to switch to free (not shown on the map). Lower values switch sooner; higher values delay the transition. Range: 1 to 1000 frames.',
		})}
	</Text>
	<Input
		type="number"
		min="1"
		max="1000"
		step="1"
		bind:value={actFreeThr}
		label={$format('presence_fine_tuning_act_free_label', {
			default: 'Tracking loss threshold (act_free_thr)',
		})}
		id="input-act-free-thr-settings"
		aria-labelledby="input-act-free-thr-settings"
	/>

	<Text styleType="body" class="mb-xs mt-s">
		{$format('presence_fine_tuning_stat_free_label', {
			default: 'Stillness tracking threshold (stat_free_thr)',
		})}
	</Text>
	<Text styleType="body-small" class="text-color-muted mb-s">
		{$format('presence_fine_tuning_stat_free_tooltip', {
			default:
				'Set the frames required to confirm a stationary object as presence. Higher values improve the detection of stationary objects. Range: 1 to 1000 frames.',
		})}
	</Text>
	<Input
		type="number"
		min="1"
		max="1000"
		step="1"
		bind:value={statFreeThr}
		label={$format('presence_fine_tuning_stat_free_label', {
			default: 'Stillness tracking threshold (stat_free_thr)',
		})}
		id="input-stat-free-thr-settings"
		aria-labelledby="input-stat-free-thr-settings"
	/>

	<Text styleType="body" class="mb-xs mt-s">
		{$format('presence_fine_tuning_sleep_free_label', {
			default: 'Stillness timeout threshold (sleep_free_thr)',
		})}
	</Text>
	<Text styleType="body-small" class="text-color-muted mb-s">
		{$format('presence_fine_tuning_sleep_free_tooltip', {
			default:
				'Set the maximum time to hold a detection when no motion is sensed. Higher values maintain presence during long inactivity of stationary objects. Range: 1 to 65535 frames.',
		})}
	</Text>
	<Input
		type="number"
		min="1"
		max="65535"
		step="1"
		bind:value={sleepFreeThr}
		label={$format('presence_fine_tuning_sleep_free_label', {
			default: 'Stillness timeout threshold (sleep_free_thr)',
		})}
		id="input-sleep-free-thr-settings"
		aria-labelledby="input-sleep-free-thr-settings"
	/>

	{#if !$showNewLayout}
		<hr class="mt-m mb-s" />
		<Button loading={waitForResponse} size="m" width="full" on:click={save} class="mb-s">
			{$format('button_save', { default: 'Save' })}
		</Button>
	{:else}
		<div class="presence-config-footer">
			<Button loading={waitForResponse} size="m" width="full" on:click={save}>
				{$format('button_save', { default: 'Save' })}
			</Button>
		</div>
	{/if}
</Stack>

<style>
	.presence-config-footer {
		position: sticky;
		bottom: 0;
		padding: 12px 0;
		background: var(--color-surface, var(--color-base));
		border-top: 1px solid var(--color-border, rgba(255, 255, 255, 0.12));
		z-index: 1;
	}
</style>
